/**
 * Context Window Hook
 *
 * Tracks token usage across the conversation to show how much of the
 * context window is filled. Helps users know when to summarize or start a new thread.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useEffect } from "react";
import type { Message } from "@/types/chat";
import type { AIModel } from "@/lib/apiKeyStorage";
import { AVAILABLE_MODELS } from "@/lib/apiKeyStorage";
import { calculateMessageTokens } from "@/lib/tokenCounter";
import { logTokenDebug } from "@/lib/tokenDebug";
import { useDebouncedValue } from "./useDebouncedValue";
type UseContextWindowOptions = {
  messages: Message[];
  currentModel: AIModel;
};
type UseContextWindowResult = {
  totalTokens: number;
  maxTokens: number;
  percentage: number;
  isNearLimit: boolean; // >= 75% (getting close)
  isAtLimit: boolean; // >= 90% (critical - stop now!)
  shouldSummarize: boolean; // >= 80% (time to summarize)
  modelInfo?: typeof AVAILABLE_MODELS[number]; // For debugging
};
export function useContextWindow(options) {
  const $ = _c(18);
  const {
    messages,
    currentModel
  } = options;
  const messageCount = messages.length;
  const debouncedMessageCount = useDebouncedValue(messageCount, 500);
  let t0;
  if ($[0] !== currentModel) {
    t0 = AVAILABLE_MODELS.find(m => m.value === currentModel);
    $[0] = currentModel;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const modelInfo = t0;
  const maxTokens = modelInfo?.contextWindow || 128000;
  let totalTokens;
  if ($[2] !== messages) {
    totalTokens = 0;
    let hasActualTokens = false;
    messages.forEach(message => {
      if (message.role === "assistant" && message.total_tokens) {
        totalTokens = totalTokens + message.total_tokens;
        totalTokens;
        hasActualTokens = true;
      } else {
        totalTokens = totalTokens + calculateMessageTokens(message.content, message.attachments);
        totalTokens;
      }
    });
    totalTokens = totalTokens + 500;
    $[2] = messages;
    $[3] = totalTokens;
  } else {
    totalTokens = $[3];
  }
  totalTokens;
  const percentage = totalTokens / maxTokens * 100;
  const t1 = percentage >= 75;
  const t2 = percentage >= 90;
  const t3 = percentage >= 80;
  let t4;
  if ($[4] !== maxTokens || $[5] !== modelInfo || $[6] !== percentage || $[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== totalTokens) {
    t4 = {
      totalTokens,
      maxTokens,
      percentage,
      isNearLimit: t1,
      isAtLimit: t2,
      shouldSummarize: t3,
      modelInfo
    };
    $[4] = maxTokens;
    $[5] = modelInfo;
    $[6] = percentage;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = totalTokens;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const result = t4;
  let t5;
  if ($[12] !== messages || $[13] !== result) {
    t5 = () => {
      if (messages.length > 0 && result.modelInfo) {
        logTokenDebug(messages, result.modelInfo.label, result.maxTokens);
      }
    };
    $[12] = messages;
    $[13] = result;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== currentModel || $[16] !== messages.length) {
    t6 = [messages.length, currentModel];
    $[15] = currentModel;
    $[16] = messages.length;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useEffect(t5, t6);
  return result;
}